import axios from "axios";

import type {
	Artifact,
	ArtifactEditMode,
	Content,
	ContentMimeType,
	DocGenSelectionPayload,
	DocumentFormattingHistory,
	MarkdownSectionGroup,
} from "@virtual-brain-lib/types";
import { camelToSnake, hasArtifactProcessingOperation } from "@virtual-brain-lib/utils-common";

import env from "../environments";
import { queryOptions } from "../query-client";
import type { QueryFunctionContext } from "../query-client/queryOption";

export const getArtifact = queryOptions({
	async queryFn({ queryKey }: QueryFunctionContext<"artifact">) {
		const [, { artifactId }] = queryKey;
		const response = await axios.get<Artifact>(`${env.apiUrl}/artifacts/${artifactId}`);

		return response.data;
	},
	queryKey: "artifact",
	staleTime: 10 * 1000,
	refetchOnWindowFocus: true,
	refetchInterval: (query) =>
		hasArtifactProcessingOperation(query.state.data?.operations) ? 5 * 1000 : false,
});

type FollowUpArtifactParams = {
	readonly artifactId: string;
	readonly query: string;
	readonly selectedText: string;
	readonly chatId: string;
	readonly mode: ArtifactEditMode;
	readonly selectedSections: string[];
	readonly selectedSectionContents: string;
	readonly originalQuery: string;
	readonly contentIds?: string[] | undefined;
	readonly brainIds?: string[] | undefined;
	readonly prevMarkdownContent: string;
};

type FollowUpArtifactResult = MarkdownSectionGroup & {
	undoPatch?: string;
	redoPatch?: string;
	newMarkdownContent: string;
	contents?: Content[];
};

/** Present when the artifact belongs to an application session, whose lastMessageAt got bumped. */
type TouchedSession = { id: string; lastMessageAt: string };

// Async modes (enrich-with-web) ack with this and stream the real result over the
// websocket. Sync modes return a FollowUpArtifactResult inline instead.
type FollowUpArtifactProcessing = { status: "processing"; session?: TouchedSession };

export type FollowUpArtifactResponse = FollowUpArtifactResult | FollowUpArtifactProcessing;

export const isFollowUpProcessing = (
	response: FollowUpArtifactResponse,
): response is FollowUpArtifactProcessing => "status" in response;

export async function followUpArtifact({
	artifactId,
	query,
	selectedText,
	chatId,
	mode,
	selectedSections,
	selectedSectionContents,
	originalQuery,
	contentIds,
	brainIds,
	prevMarkdownContent: _, // used to recalculate highlight in mutation
}: FollowUpArtifactParams) {
	const endpoint = `${env.apiUrl}/artifacts/${artifactId}/${camelToSnake(mode)}`;

	const payload = {
		query,
		selectedText,
		chatId,
		selectedSections,
		contentIds,
		brainIds,
		selectedSectionContents,
		originalQuery,
	};

	const response = await axios.post<FollowUpArtifactResponse>(endpoint, payload);

	return response.data;
}

type NavigateVersionParams = {
	readonly id: string;
	readonly direction: "undo" | "redo";
};

type NavigateVersionResponse = {
	content: string;
	undoPatch: string | undefined;
	redoPatch: string | undefined;
};

export async function navigateArtifactVersion({ id, direction }: NavigateVersionParams) {
	const endpoint = `${env.apiUrl}/artifacts/${id}/navigate`;

	const response = await axios.post<NavigateVersionResponse>(endpoint, { direction });

	return response.data;
}

type SaveArtifactContentParams = {
	readonly id: string;
	readonly content: string;
};

/** Manual edits are versioned like AI ones, so undo/redo has a single stack. */
export async function saveArtifactContent({ id, content }: SaveArtifactContentParams) {
	const endpoint = `${env.apiUrl}/artifacts/${id}/content`;

	const response = await axios.post<NavigateVersionResponse & { session?: TouchedSession }>(
		endpoint,
		{ content },
	);

	return response.data;
}

export async function exploreMoreDocuments({
	artifactId,
	userMessageId,
	assistantMessageId,
}: {
	artifactId: string;
	userMessageId: string;
	assistantMessageId: string;
}) {
	await axios.post(`${env.apiUrl}/artifacts/${artifactId}/explore-more-documents`, {
		userMessageId,
		assistantMessageId,
	});
}

export interface ArtifactDocument {
	id: string;
	mimeType: string;
	title: string;
	documentFormattingHistory: DocumentFormattingHistory;
}

/** One specific document, by its own id. */
export const getArtifactDocument = queryOptions({
	async queryFn({ queryKey }: QueryFunctionContext<"artifactDocument">) {
		const [, { artifactId, documentId }] = queryKey;
		const response = await axios.get<ArtifactDocument>(
			`${env.apiUrl}/artifacts/${artifactId}/document/${documentId}`,
		);

		return response.data;
	},
	queryKey: "artifactDocument",
});

/**
 * A brand new document of this mimeType, regardless of whether the artifact already has one. Used by
 * Format & export, so going through it again never just reopens what is already there.
 */
export async function createArtifactDocument({
	artifactId,
	mimeType,
	templateId,
}: {
	artifactId: string;
	mimeType: ContentMimeType;
	templateId?: string;
}): Promise<ArtifactDocument> {
	const response = await axios.post<ArtifactDocument>(
		`${env.apiUrl}/artifacts/${artifactId}/document`,
		{ mimeType, templateId },
	);

	return response.data;
}

/** The document as it currently stands. */
export const getArtifactDocumentPreview = queryOptions({
	async queryFn({ queryKey }: QueryFunctionContext<"artifactDocumentPreview">) {
		const [, { artifactId, documentId }] = queryKey;
		const response = await axios.get<Blob>(
			`${env.apiUrl}/artifacts/${artifactId}/document-preview`,
			{ params: { documentId }, responseType: "blob" },
		);

		return response.data;
	},
	queryKey: "artifactDocumentPreview",
	// The key is stable across edits, so the query needs an explicit invalidation to refetch after
	// each formatting turn instead of relying on a new key to trigger it.
	staleTime: Infinity,
	// A run can finish while the modal is closed, which misses the event that invalidates the key.
	refetchOnMount: "always",
	retry: false,
	// Holding the previous bytes keeps the document on screen instead of blanking it while the
	// refetch triggered by that invalidation is in flight.
	placeholderData: (previous: Blob | undefined) => previous,
});

export async function sendDocumentStyleMessage({
	artifactId,
	documentId,
	query,
	selections,
}: {
	artifactId: string;
	documentId: string;
	query: string;
	selections?: DocGenSelectionPayload[] | undefined;
}) {
	const response = await axios.post<{ documentFormattingHistory: DocumentFormattingHistory }>(
		`${env.apiUrl}/artifacts/${artifactId}/document-style`,
		{ documentId, query, selections },
	);

	return response.data;
}
